import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiMapPin, FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Allgemeine Anfrage',
    message: '',
    privacy: false
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy) return;
    setStatus({ loading: true, success: false, error: null });
    try {
      const response = await fetch('/send_mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (response.ok) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', subject: 'Allgemeine Anfrage', message: '', privacy: false });
      } else {
        throw new Error(result.message || 'Etwas ist schiefgelaufen.');
      }
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-navy-950 transition-colors duration-300">
      {/* Hero */}
      <div className="bg-brand dark:bg-navy-900 text-white py-24 relative overflow-hidden border-b border-transparent dark:border-navy-800">
        <div className="absolute inset-0 opacity-10">
          <img src="/images/contact-bg.jpg" className="w-full h-full object-cover" alt="Background" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-serif font-bold mb-6"
          >
            Kontaktieren Sie uns
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-xl text-stone-300 max-w-2xl mx-auto font-light"
          >
            Wir freuen uns auf den Austausch mit Autoren und Partnern des Vidento Verlags.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: contact info */}
          <div className="lg:col-span-4 space-y-12">
            <div>
              <h2 className="text-2xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-6">Direktkontakt</h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center text-amber-800 dark:text-amber-400">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-800 dark:text-stone-200">Verlagsbüro</h4>
                    <p className="text-stone-600 dark:text-stone-400">Föreningen Vidento<br />Örträsk 48<br />935 93 Norsjö (Schweden)</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center text-amber-800 dark:text-amber-400">
                    <SafeIcon icon={FiMail} className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-800 dark:text-stone-200">E-Mail</h4>
                    <p className="text-stone-600 dark:text-stone-400">post@vidento.eu</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-amber-900 dark:bg-amber-800 text-white p-8 rounded-sm shadow-xl">
              <h3 className="text-xl font-serif font-bold mb-4">Manuskripte</h3>
              <p className="text-amber-100 text-sm leading-relaxed">Senden Sie uns Ihr Exposé per E-Mail an post@vidento.eu.</p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-navy-900 p-8 md:p-12 rounded-lg shadow-sm border border-stone-200 dark:border-navy-800"
            >
              <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-8">
                Schreiben Sie uns eine Nachricht
              </h3>

              {status.success ? (
                <div className="bg-green-50 dark:bg-green-900/20 p-8 text-center rounded-md">
                  <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-green-900 dark:text-green-400 mb-2">Gesendet!</h4>
                  <button
                    onClick={() => setStatus({ ...status, success: false })}
                    className="text-green-800 dark:text-green-400 font-bold hover:underline"
                  >
                    Weitere Nachricht
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status.error && (
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-sm">
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
                      {status.error}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input
                      type="text" required placeholder="Ihr Name"
                      className="p-3 bg-stone-50 dark:bg-navy-800 border border-stone-200 dark:border-navy-700 rounded-md outline-none text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:border-amber-700 dark:focus:border-amber-500 transition-colors"
                      value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                    <input
                      type="email" required placeholder="E-Mail Adresse"
                      className="p-3 bg-stone-50 dark:bg-navy-800 border border-stone-200 dark:border-navy-700 rounded-md outline-none text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:border-amber-700 dark:focus:border-amber-500 transition-colors"
                      value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <textarea
                    required rows="6" placeholder="Ihre Nachricht"
                    className="w-full p-3 bg-stone-50 dark:bg-navy-800 border border-stone-200 dark:border-navy-700 rounded-md outline-none text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:border-amber-700 dark:focus:border-amber-500 transition-colors"
                    value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })}
                  />
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox" required className="mt-1 w-4 h-4 accent-stone-900 dark:accent-amber-500 flex-shrink-0"
                      checked={formData.privacy} onChange={e => setFormData({ ...formData, privacy: e.target.checked })}
                    />
                    <span className="text-sm text-stone-600 dark:text-stone-400">
                      Ich habe die{' '}
                      <a href="#/dsgvo" className="underline hover:text-stone-900 dark:hover:text-stone-200">
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und stimme der Verarbeitung meiner Daten zu.*
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={status.loading || !formData.privacy}
                    className="bg-brand dark:bg-amber-700 text-white px-8 py-3 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-light dark:hover:bg-amber-600 transition-colors flex items-center gap-2"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    {status.loading ? 'Wird gesendet…' : 'Nachricht senden'}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
